"use client";

import { GalleryOptions } from "@/types/wordpress";
import { useContext, useEffect, useState } from "react";
import styles from "@/styles/modules/components/gallery/galleryList.module.scss";
import Image from "next/image";
import { LanguageContext } from "@/context/languageContext";
import { translate as translateString } from "@/utils/translate";

interface GalleryListProps {
  images: GalleryOptions[];
}

export default function GalleryList({ images }: GalleryListProps) {
  const { language } = useContext(LanguageContext);
  const [currentCategory, setCurrentCategory] = useState<string>("all");
  const [translate, setTranslate] = useState<number>(0);
  const [counter, setCounter] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [currentImages, setCurrentImages] = useState<GalleryOptions[]>(images);
  const [stopAnimation, setStopAnimation] = useState<boolean>(false);

  const [isMobile, setIsMobile] = useState(false); // Default to mobile size

  const categories: { name: string; slug: string }[] = [];

  // Join all image categories
  images.map((image) => {
    image.category.nodes.map((cat) => {
      categories.push(cat);
    });
  });

  // Remove duplicated categories and sort by asc
  const uniqueCategories = categories
    .map((cat) => ({
      ...cat,
      name: cat.name
    }))
    .filter(
      (obj, index) =>
        categories.findIndex((cat) => cat.name === obj.name) === index
    )
    .sort((a, b) => a.name.localeCompare(b.name));


  const handleSetCategory = (cat: string) => {
    setCurrentCategory(cat);
    setTranslate(0);
    setCounter(0);

    setCurrentImages(
      images.filter((image) => {
        return isCurrentCategory(image, cat);
      })
    );
  };

  const isCurrentCategory = (image: GalleryOptions, cat: string) => {
    return (
      image.category.nodes.filter((image) => image.slug === cat).length > 0 ||
      cat === "all"
    );
  };

  const handleImageFullscreen = (counter: number) => {
    setCounter(counter);
    setTranslate(100 * counter);
    setIsFullscreen(true);
  };

  useEffect(() => {
    if (isFullscreen) {
      setStopAnimation(true);
      setTimeout(() => {
        setStopAnimation(false);
      }, 100);
    } else {
      setStopAnimation(false);
    }
  }, [isFullscreen]);

  useEffect(() => {
    setCurrentCategory("all");
    setCurrentImages(images);
  }, [images]);

  const pauseVideos = () => {
    document.querySelectorAll("video").forEach((vid) => vid.pause());
  };

  const handleLeft = () => {
    if (counter > 0) {
      setTranslate((prevValue) => prevValue - 100);
      setCounter((prevValue) => prevValue - 1);
      pauseVideos();
    }
  };

  const handleRight = () => {
    if (counter < currentImages.length - 1) {
      setTranslate((prevValue) => prevValue + 100);
      setCounter((prevValue) => prevValue + 1);
      pauseVideos();
    }
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth < 769);
  };

  useEffect(() => {
    setIsMobile(window.innerWidth < 769);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, [isMobile]);


  return (
    <div className={styles.galleryList}>
      <section className={styles.selector} data-aos="fadeIn">
        <span
          onClick={() => handleSetCategory("all")}
          className={currentCategory === "all" ? styles.current : ""}
        >
          {translateString["all"][language]}
        </span>
        {uniqueCategories &&
          uniqueCategories.map((cat, i) => (
            <span
              key={i}
              onClick={() => handleSetCategory(cat.slug)}
              className={currentCategory === cat.slug ? styles.current : ""}
            >
              {(language === 'de' && cat.name.includes(" DE")) ? cat.name.slice(0, cat.name.lastIndexOf(" DE")).trim() : cat.name}
            </span>
          ))}
      </section>
      <section className={styles.gallery}>
        {currentImages.map((image, i) => (
          <article
            key={i}
            className={
              isCurrentCategory(image, currentCategory) ? styles.active : ""
            }
            style={{
              gridColumn: `span ${isMobile ? '' : (image.columns || 3)}`,
              gridRow: `span ${isMobile ? '' : (image.rows || 1)}`,
            }}
          >
            <Image
              alt={image.image.node.altText || "Imperial Riding School"}
              src={image.image.node.link}
              width={image.image.node.mediaDetails.width}
              height={image.image.node.mediaDetails.height}
              onClick={() => handleImageFullscreen(i)}
              data-aos="fadeIn" data-aos-delay={100 * i}
            />
          </article>
        ))}
      </section>
      <section
        className={`${styles.fullscreen} ${isFullscreen ? styles.active : ""}`}
      >
        <div className={styles.icons}>
          <span className={styles.close}>
            <Image
              alt="Close icon"
              src="/icons/close-icon.svg"
              width={41}
              height={31}
              onClick={() => {
                setIsFullscreen(false);
                pauseVideos();
              }}
            />
          </span>
          <span
            className={`${styles.arrow} ${styles.arrowLeft} ${counter === 0 ? styles.disabled : ""
              }`}
          >
            <Image
              alt="Arrow left icon"
              src="/icons/arrow-left-black-icon.svg"
              width={15}
              height={31}
              onClick={handleLeft}
            />
          </span>
          <span
            className={`${styles.arrow} ${styles.arrowRight} ${counter === currentImages.length - 1 ? styles.disabled : ""
              }`}
          >
            <Image
              alt="Arrow right icon"
              src="/icons/arrow-right-black-icon.svg"
              width={15}
              height={31}
              onClick={handleRight}
            />
          </span>
        </div>
        <div className={styles.images}>
          <div
            className={styles.imagesContainer}
            style={{
              transform: `translateX(-${translate}%)`,
              transition: stopAnimation ? styles.stopAnimation : "",
            }}
          >
            {currentImages.map((image, i) => (
              <div
                className={`${styles.imageContainer} ${isCurrentCategory(image, currentCategory) ? styles.active : ""
                  }`}
                key={i}
              >
                <Image
                  key={i}
                  alt={image.image!.node.altText || "Imperial Riding School"}
                  src={image.image!.node.link}
                  width={image.image!.node.mediaDetails.width}
                  height={image.image!.node.mediaDetails.height}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.texts}>
          <div
            className={styles.textsContainer}
            style={{ transform: `translateX(-${translate}%)` }}
          >
            {images.map((image, i) => (
              <div
                key={i}
                dangerouslySetInnerHTML={{
                  __html: image.image!.node.description || "",
                }}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
